import { motion } from "framer-motion";
import { Check, X, ChevronDown, Mail, Users, TrendingUp, Megaphone, GraduationCap, Bot, UserCheck, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SubstackHeaderButton } from "@/components/SubstackLink";

const HERO_BG = "https://customer-assets.emergentagent.com/job_ridgemont-leads/artifacts/9xt4hwqb_Troon%20North.jpg";

const MAILTO_SENIOR = "mailto:hello@ridgemontfinancial.com?subject=Experienced%20Advisor%20Track%20%E2%80%94%2070%2F30%20Split&body=Hi%20Brandi%20Jo%2C%0A%0AI%27m%20applying%20for%20the%20Experienced%20Advisor%20track%20(70%2F30%20split).%0A%0ALink%20to%20my%20presentation%2Fportfolio%20%2F%20videos%20%2F%20social%20(required)%3A%0A%0A---%0ACurrent%20carrier%2Fcompany%3A%0AYears%20licensed%3A%0AAverage%20monthly%20closed%20premium%20(last%206%20mo)%3A%0AKOLBE%20MO%20(if%20known)%3A%0AInterest%20in%20leading%20a%20pod%2Fteam%3A%0A%0AThanks%2C";

const MAILTO_JUNIOR = "mailto:hello@ridgemontfinancial.com?subject=Ramp%20Advisor%20Track%20%E2%80%94%2050%2F50%20Split&body=Hi%20Brandi%20Jo%2C%0A%0AI%27m%20applying%20for%20the%20Ramp%20Advisor%20track%20(50%2F50%20split%20%2B%20full%20firm-paid%20marketing%20%26%20training).%0A%0AStates%20licensed%20(Life%20%26%20Health)%3A%0AMonths%2Fyears%20licensed%3A%0ACurrent%20role%2Fcompany%3A%0ATypical%20monthly%20annuity%20premium%20closed%20today%20(if%20any)%3A%0AWhy%20you%27re%20looking%20to%20level%20up%3A%0AAnything%20about%20you%20a%20resume%20wouldn%27t%20show%3A%0A%0AThanks%2C";

const fadeUp = { initial: { opacity: 0, y: 40 }, whileInView: { opacity: 1, y: 0 }, transition: { duration: 0.8 }, viewport: { once: true } };

const AgentsPage = () => {
  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div>
      {/* Hero — matches homepage layout */}
      <section className="hero-section">
        <div className="hero-bg">
          <img src={HERO_BG} alt="Troon North Golf Course" />
          <div className="hero-overlay" />
        </div>

        <div className="absolute top-0 left-0 w-full py-4 px-6 z-20 flex items-center justify-between">
          <p className="text-white/40 text-xs tracking-[0.2em] uppercase">Now Hiring &middot; Telewealth Advisors &middot; 1099 Independent Contractor</p>
          <SubstackHeaderButton />
        </div>

        <div className="hero-content max-w-[1400px] mx-auto px-4 md:px-8 pt-32 pb-20">
          <motion.div
            className="max-w-2xl bg-[#0A192F]/75 backdrop-blur-sm p-10 md:p-14"
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <motion.p
              className="text-sm md:text-base tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              Ridgemont Financial
            </motion.p>

            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-light text-white leading-[1.1] tracking-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Build your career.
              <span className="block text-[#C5A059]">Or build a team.</span>
            </h1>

            <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-10 max-w-2xl">
              I'm building a virtual advisory team — two tracks, one standard.
              Pre-qualified appointments. Independent carrier access. Real 1099 independence
              with the pipeline already built for you. Whether you're proven or proving it,
              there's a seat here for the right wiring.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a href={MAILTO_SENIOR}>
                <Button className="btn-accent px-8 py-6 text-lg w-full sm:w-auto" data-testid="hero-senior-btn">
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Experienced &middot; 70/30 Split
                </Button>
              </a>
              <a href={MAILTO_JUNIOR}>
                <Button
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg w-full sm:w-auto"
                  data-testid="hero-junior-btn"
                >
                  <Users className="w-5 h-5 mr-2" />
                  Ramp Advisor &middot; 50/50 Split
                </Button>
              </a>
            </div>
            <button
              onClick={() => scrollToSection("tracks")}
              className="mt-6 text-white/50 hover:text-[#C5A059] transition-colors text-sm tracking-wider"
              data-testid="hero-explore-tracks"
            >
              Explore both tracks &rarr;
            </button>
          </motion.div>

          <motion.div
            className="absolute bottom-10 left-1/2 -translate-x-1/2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
          >
            <ChevronDown className="w-8 h-8 text-white/50 animate-bounce" />
          </motion.div>
        </div>
      </section>

      {/* Who's Hiring */}
      <section className="py-24 md:py-32 bg-[#0A192F]">
        <div className="max-w-[1000px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp}>
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">Who's Hiring</p>
            <h2 className="text-4xl md:text-5xl font-light text-white tracking-tight mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              I've built the room you'll close in.
            </h2>
            <div className="text-lg text-white/70 leading-relaxed space-y-6">
              <p>
                Thirty years inside the financial services industry. I've recruited, scaled, and led advisor teams
                from 70 to 150 at the corporate level and 55 across Texas at the regional level. Full P&L responsibility,
                full stack — recruiting, onboarding, retention, production.
              </p>
              <p>
                I built a lead generation platform to an 8-figure valuation in under twelve months, exited successfully,
                and today run Ridgemont Financial — a boutique virtual planning firm.
              </p>
              <p className="text-white font-medium">
                I know what makes producers thrive — at every stage of a career. I know what kills them.
                That's why this team is built the way it is.
              </p>
            </div>
          </motion.div>

          <div className="grid grid-cols-3 gap-6 mt-16">
            {[
              { num: "150", label: "Advisor team built & led" },
              { num: "8-fig", label: "Lead gen platform exit, under 12 months" },
              { num: "10 yrs", label: "Pro tour, 6 continents — built on process" }
            ].map((s, i) => (
              <motion.div key={i} className="text-center bg-white/5 border border-white/10 p-6"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }} viewport={{ once: true }}>
                <p className="text-4xl md:text-5xl font-light text-[#C5A059]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.num}</p>
                <p className="text-sm text-white/50 mt-2">{s.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Setup */}
      <section className="py-24 md:py-32 bg-[#FAFAF9]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">The Setup</p>
            <h2 className="text-4xl md:text-5xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Most agents hunt. Our advisors just advise.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: "5-7 appointments a day", desc: "Pre-qualified, pre-booked. You step in already on the phone with the right people at the right moment." },
              { title: "Zero costs. Zero exceptions.", desc: "No leads to buy. No marketing spend. No overhead. No CRM bills. The pipeline is built and paid for." },
              { title: "Show up. Just advise.", desc: "Marketing, lead gen, nurture, follow-up, compliance — handled. Your only job is the conversation that converts." }
            ].map((item, i) => (
              <motion.div key={i} className="bg-white p-8 md:p-10 border border-[#E7E5E4] relative overflow-hidden"
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }} viewport={{ once: true }}>
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#C5A059] to-[#E6C88B]" />
                <h3 className="text-xl font-medium text-[#0A192F] mb-3">{item.title}</h3>
                <p className="text-[#57534E] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Two Tracks */}
      <section id="tracks" className="py-24 md:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">Two Tracks &middot; Fully Transparent</p>
            <h2 className="text-4xl md:text-5xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Two completely different producers.
              <span className="block text-[#C5A059]">Two honest structures.</span>
            </h2>
            <p className="text-lg text-[#57534E] mt-4 max-w-2xl mx-auto">
              Same product shelf. Same lead flow. Same 1099 independence.
              The split &mdash; and what the firm covers &mdash; depends on where you are today.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
            {/* EXPERIENCED TRACK */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-[#0A192F] p-10 md:p-12 relative overflow-hidden border-2 border-[#C5A059]"
              data-testid="track-senior"
            >
              <div className="absolute top-0 right-0 bg-[#C5A059] text-[#0A192F] px-4 py-1 text-xs tracking-[0.2em] uppercase font-semibold">
                Proven Producer
              </div>
              <TrendingUp className="w-12 h-12 text-[#C5A059] mb-6" />
              <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-3">Track 01 &middot; Experienced</p>
              <h3 className="text-3xl md:text-4xl font-light text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Experienced Advisor
              </h3>
              <p className="text-white/70 mb-6 leading-relaxed">
                For the advisor <span className="text-white font-medium">consistently closing $250K+ per month in annuity premium</span>.
                You have a proven sales process, your own presentation, and a track record you can put on the table.
              </p>

              {/* The Split */}
              <div className="bg-[#C5A059]/10 border-l-4 border-[#C5A059] p-5 mb-8" data-testid="senior-split-block">
                <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-2">The Split</p>
                <p className="text-3xl font-light text-white mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  70% to you <span className="text-white/40">/</span> 30% to firm
                </p>
                <p className="text-white/60 text-sm leading-relaxed mt-2">
                  You keep the majority. The firm provides the pre-qualified appointments &mdash; you bring the close.
                </p>
              </div>

              <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059]/80 font-semibold mb-4">Who this is for</p>
              <ul className="space-y-3 mb-8">
                {[
                  "Consistently closing $250K+/month in annuity premium (verifiable)",
                  "You bring your own sales process and closing presentation",
                  "2+ years actively writing annuity business",
                  "Ready to plug into 5&ndash;7 pre-qualified appointments a day and scale"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/80 text-sm">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#C5A059]" />
                    <span dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>

              {/* Required to apply */}
              <div className="bg-white/5 border border-white/10 p-5 mb-8" data-testid="senior-required-block">
                <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-3">Required with your email</p>
                <p className="text-white/80 text-sm leading-relaxed">
                  A link to your <span className="text-white font-medium">presentation, video portfolio, social media, or anything else</span> that
                  demonstrates your closing process. If you can't show us how you sell, this isn't the seat.
                </p>
              </div>

              <a href={MAILTO_SENIOR}>
                <Button className="bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 px-8 py-6 text-base font-semibold w-full" data-testid="senior-email-btn">
                  <Mail className="w-5 h-5 mr-2" />
                  Email hello@ridgemontfinancial.com
                </Button>
              </a>
              <p className="text-white/40 text-xs mt-3 text-center">
                Include your portfolio link in the email.
              </p>
            </motion.div>

            {/* RAMP / INEXPERIENCED TRACK */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-[#1B4D3E] p-10 md:p-12 relative overflow-hidden border-2 border-white/10"
              data-testid="track-junior"
            >
              <div className="absolute top-0 right-0 bg-white/10 text-white px-4 py-1 text-xs tracking-[0.2em] uppercase font-semibold">
                Firm-Backed Ramp
              </div>
              <Users className="w-12 h-12 text-[#C5A059] mb-6" />
              <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-3">Track 02 &middot; Ramp</p>
              <h3 className="text-3xl md:text-4xl font-light text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Ramp Advisor
              </h3>
              <p className="text-white/85 mb-6 leading-relaxed">
                For any licensed advisor <span className="text-white font-medium">not yet consistently closing $250K/month in annuity premium</span>.
                You have the license and the drive &mdash; the firm provides the system that gets you there.
              </p>

              {/* The Split */}
              <div className="bg-[#C5A059]/15 border-l-4 border-[#C5A059] p-5 mb-6" data-testid="junior-split-block">
                <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-2">The Split</p>
                <p className="text-3xl font-light text-white mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  50% to you <span className="text-white/40">/</span> 50% to firm
                </p>
                <p className="text-white/70 text-sm leading-relaxed mt-2">
                  The firm invests heavily in you &mdash; marketing, training, coaching, tech.
                  Fair split for a seat where everything is provided.
                </p>
              </div>

              {/* Benefits Icon Grid */}
              <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">What the firm provides</p>
              <div className="grid grid-cols-5 gap-2 mb-6" data-testid="junior-benefits-icons">
                {[
                  { icon: Megaphone, label: "Marketing" },
                  { icon: GraduationCap, label: "Training" },
                  { icon: Bot, label: "AI Playbook" },
                  { icon: UserCheck, label: "Coaching" },
                  { icon: BookOpen, label: "Client Ed." }
                ].map(({ icon: Icon, label }, i) => (
                  <div key={i} className="flex flex-col items-center text-center p-3 bg-white/5 border border-white/10">
                    <Icon className="w-6 h-6 text-[#C5A059] mb-2" />
                    <p className="text-[10px] md:text-xs text-white/70 tracking-wide leading-tight">{label}</p>
                  </div>
                ))}
              </div>

              <ul className="space-y-3 mb-8">
                {[
                  "<span class='text-white font-medium'>Firm-paid marketing</span> &mdash; 5&ndash;7 pre-qualified appointments delivered daily, zero cost to you",
                  "<span class='text-white font-medium'>200+ on-demand video lessons</span> covering carriers, products, objections, closing frameworks",
                  "<span class='text-white font-medium'>The Ridgemont AI Playbook</span> &mdash; carrier illustrations, objection responses, and case design on demand",
                  "<span class='text-white font-medium'>1:1 coaching</span> from a senior producer, recorded-call reviews, and live appointment co-pilot",
                  "<span class='text-white font-medium'>Client education library</span> you can share with prospects &mdash; already compliance-reviewed"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/90 text-sm">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#C5A059]" />
                    <span dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>

              <a href={MAILTO_JUNIOR}>
                <Button className="bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 px-8 py-6 text-base font-semibold w-full" data-testid="junior-email-btn">
                  <Mail className="w-5 h-5 mr-2" />
                  Email hello@ridgemontfinancial.com
                </Button>
              </a>
              <p className="text-white/50 text-xs mt-3 text-center">
                No portfolio required &mdash; just an honest snapshot of where you are today.
              </p>
            </motion.div>
          </div>

          <motion.p {...fadeUp} className="text-center text-[#57534E]/70 text-sm mt-12 max-w-2xl mx-auto italic">
            Both tracks share the same 1099 independence, carrier shelf, lead flow, and
            daily commission payouts. The split reflects what the firm is providing &mdash; nothing hidden.
          </motion.p>
        </div>
      </section>

      {/* Position Structure */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-[1100px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">The Position</p>
            <h2 className="text-4xl md:text-5xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              1099 Independent Contractor.
              <span className="block text-[#C5A059]">Full Autonomy. Fair Structure.</span>
            </h2>
            <p className="text-lg text-[#57534E] mt-4 max-w-2xl mx-auto">
              You run your book. You own your income. You keep your independence.
              We hand you a system that would take years to build alone &mdash; and get out of your way.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {[
              {
                title: "1099 Independent Contractor",
                desc: "You're a licensed professional, not an employee. You set your own schedule, control your own approach, and receive commission income on a 1099 at year end. Independence, unambiguous and real."
              },
              {
                title: "The Pipeline Is Provided",
                desc: "5&ndash;7 pre-qualified appointments delivered to your calendar every day. No cold calls. No lead-buying. No marketing spend. The only thing you bring is the conversation that closes."
              },
              {
                title: "Deductible Business Expenses",
                desc: "As a 1099 producer, you report income on Schedule C and can deduct ordinary and necessary business expenses &mdash; home office, mileage, equipment, licensing, continuing education, and more. Your CPA will love you."
              }
            ].map((item, i) => (
              <motion.div key={i} className="bg-[#FAFAF9] border border-[#E7E5E4] p-8 relative overflow-hidden"
                initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }} viewport={{ once: true }}>
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#C5A059] to-[#E6C88B]" />
                <h3 className="text-lg font-medium text-[#0A192F] mb-3">{item.title}</h3>
                <p className="text-[#57534E] leading-relaxed text-sm" dangerouslySetInnerHTML={{ __html: item.desc }} />
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="bg-[#0A192F] text-white p-8 md:p-10 border-l-4 border-[#C5A059]">
            <p className="text-xs tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-3">What that means in plain English</p>
            <p className="text-white/80 leading-relaxed">
              You keep the independence, tax flexibility, and upside of a 1099 producer.
              You skip the part where independent producers waste their first three years building a lead pipeline from scratch.
              <span className="text-white"> Same freedom top independent shops promise &mdash; without spending 60% of your time chasing your next appointment.</span>
            </p>
            <p className="text-white/50 text-xs mt-4 italic">
              We recommend you confirm tax treatment with your CPA. Ridgemont does not provide tax or legal advice.
            </p>
          </motion.div>

          {/* Carrier Logos */}
          <motion.div {...fadeUp} className="mt-16">
            <p className="text-center text-xs tracking-[0.25em] uppercase text-[#57534E] font-semibold mb-8">
              Independent &middot; Appointed with leading carriers
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-8 items-center max-w-5xl mx-auto">
              {[
                "Allianz Life",
                "Athene",
                "North American",
                "Nationwide",
                "Pacific Life",
                "Lincoln Financial",
                "Mutual of Omaha",
                "Prudential",
                "Corebridge",
                "Transamerica",
                "F&G",
                "Symetra"
              ].map((carrier, i) => (
                <motion.div
                  key={carrier}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.04 }}
                  viewport={{ once: true }}
                  className="group flex items-center justify-center"
                  data-testid={`carrier-logo-${i}`}
                >
                  <span
                    className="text-base md:text-lg font-light tracking-[0.15em] uppercase text-[#0A192F]/45 group-hover:text-[#0A192F] transition-colors duration-300 text-center"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {carrier}
                  </span>
                </motion.div>
              ))}
            </div>
            <p className="text-center text-[#0A192F]/80 text-sm md:text-base mt-8 max-w-2xl mx-auto">
              &hellip;and <span className="text-[#C5A059] font-semibold">many more</span> carrier relationships we can activate as your book grows.
            </p>
            <p className="text-center text-[#57534E]/50 text-xs italic mt-3 max-w-2xl mx-auto">
              Carrier names shown represent partner relationships in our network. Appointments and product availability vary by state and producer credentialing.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Compensation */}
      <section className="py-16 bg-[#C5A059]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <motion.div {...fadeUp}>
            <h3 className="text-2xl md:text-3xl font-light text-[#0A192F] mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Built for a Closer
            </h3>
            <p className="text-[#0A192F]/70 mb-8 max-w-xl mx-auto">
              The pipeline is mine. The close is yours. The math is fair, transparent, and paid daily.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { title: "Uncapped", desc: "No ceiling. No territory cap. The harder you close, the bigger the number." },
              { title: "Paid daily", desc: "No weekly batch, no 60-day delays. Commissions are released each business day as policies are issued and paid." },
              { title: "Transparent splits", desc: "70/30 if you're consistently closing $250K+/mo in annuity premium. 50/50 if you're building there &mdash; with everything the firm provides." }
            ].map((item, i) => (
              <motion.div key={i} className="bg-[#0A192F] p-8 text-left"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} viewport={{ once: true }}>
                <h4 className="text-lg font-medium text-white mb-2">{item.title}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.p className="text-[#0A192F]/60 text-sm mt-8 italic max-w-2xl mx-auto" {...fadeUp}>
            No hidden overrides. No mystery cap. The split you see is the split you get.
          </motion.p>
        </div>
      </section>

      {/* The Fit */}
      <section id="fit" className="py-24 md:py-32 bg-white">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">The Fit</p>
            <h2 className="text-4xl md:text-5xl font-light text-[#0A192F] tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Both tracks share the same DNA.
            </h2>
            <p className="text-lg text-[#57534E] mt-4">
              Track-specific experience differs. Everything below is non-negotiable for either lane.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div {...fadeUp}>
              <h3 className="text-xl font-medium text-[#0A192F] mb-6">You are</h3>
              <ul className="space-y-4">
                {[
                  "Currently Life &amp; Health licensed (resident state, at minimum)",
                  "Comfortable on the phone and Zoom all day — from anywhere",
                  "Process-loyal: you follow the playbook before you rewrite it",
                  "Self-aware: you know your role and own it like a craft",
                  "Committed and consistent. This is a serious book to build, not a side gig."
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <Check className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#1B4D3E]" />
                    <span className="text-[#0A192F]" dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div {...fadeUp} transition={{ delay: 0.2 }}>
              <h3 className="text-xl font-medium text-[#0A192F] mb-6">Don't apply if</h3>
              <ul className="space-y-4">
                {[
                  "MLM background of any kind",
                  "Independent shop hopping every 18 months",
                  'Quick-start "close anything that moves" energy',
                  "Looking for a casual side gig or hobby income",
                  "Need to be sold on the opportunity (this isn't that conversation)"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <X className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-400" />
                    <span className="text-[#57534E]">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* KOLBE */}
      <section className="py-24 md:py-32 bg-[#1B4D3E]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">The KOLBE Profile</p>
            <h2 className="text-3xl md:text-4xl font-light text-white tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              How you're wired matters more than how you sell.
            </h2>
            <p className="text-white/60 mt-4">We use Kolbe A across both tracks. The MO we hire for:</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { trait: "Fact Finder", level: "High", desc: "You ask the questions most agents skip. Discovery is the close." },
              { trait: "Follow Through", level: "High", desc: "You finish what you start. Process loyalty is your edge." },
              { trait: "Implementer", level: "Mid", desc: "You execute when it's clear. You don't reinvent the room." },
              { trait: "Quick Start", level: "Low", desc: "You don't improvise the close. You honor the process." }
            ].map((k, i) => (
              <motion.div key={i} className="bg-white/10 backdrop-blur-sm p-6 border border-white/20"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} viewport={{ once: true }}>
                <p className="text-xs uppercase tracking-wider text-white/40 mb-1">{k.trait}</p>
                <p className="text-2xl font-light text-[#C5A059] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{k.level}</p>
                <p className="text-sm text-white/60 leading-relaxed">{k.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MBTI */}
      <section className="py-24 md:py-32 bg-[#0A192F]">
        <div className="max-w-[1200px] mx-auto px-4 md:px-8">
          <motion.div {...fadeUp} className="text-center mb-12">
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">The MBTI Profile</p>
            <h2 className="text-3xl md:text-4xl font-light text-white tracking-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              INTJ. The Strategist.
            </h2>
            <p className="text-white/60 mt-4">You see the system. You plan the work. You work the plan.</p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { letter: "I", name: "Introvert", desc: "Energy comes from depth, not crowds. You think before you speak — and prospects feel the difference." },
              { letter: "N", name: "Intuitive", desc: "You read between the lines. Patterns, motivations, what the prospect isn't saying — that's where you live." },
              { letter: "T", name: "Thinking", desc: "Decisions are logic-first. You close with frameworks and clarity, not pressure or charm." },
              { letter: "J", name: "Judging", desc: "Structure is freedom. You finish what you start, on time, on script, on the system." }
            ].map((m, i) => (
              <motion.div key={i} className="text-center"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} viewport={{ once: true }}>
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center border-2 border-[#C5A059]/40">
                  <span className="text-3xl font-light text-[#C5A059]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{m.letter}</span>
                </div>
                <p className="text-white font-medium mb-2">{m.name}</p>
                <p className="text-sm text-white/50 leading-relaxed">{m.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Math */}
      <section className="py-24 md:py-32 bg-[#FAFAF9]">
        <div className="max-w-[800px] mx-auto px-4 md:px-8 text-center">
          <motion.div {...fadeUp}>
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-4">The Math</p>
            <h2 className="text-4xl md:text-5xl font-light text-[#0A192F] tracking-tight mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              What happens when the chase is already done for you?
            </h2>
            <p className="text-2xl text-[#1B4D3E]">
              5-7 pre-qualified shots a day.
            </p>
            <p className="text-2xl mt-2 font-medium text-[#0A192F]">
              All you have to do is advise &mdash; and get paid daily for it.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 bg-[#0A192F] relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#C5A059]/30 rounded-full blur-[150px]" />
        </div>
        <div className="max-w-[900px] mx-auto px-4 md:px-8 text-center relative z-10">
          <motion.div {...fadeUp}>
            <p className="text-sm tracking-[0.2em] uppercase text-[#C5A059] font-semibold mb-6">Two Tracks &middot; One Team</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-white tracking-tight mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Pick your lane.
              <span className="block text-[#C5A059]">Send the note.</span>
            </h2>
            <p className="text-lg text-white/60 mb-10 max-w-xl mx-auto leading-relaxed">
              Tell me where you are, where you want to be, and which track speaks to you.
              I'll take it from there.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch">
              <a href={MAILTO_SENIOR}>
                <Button className="bg-[#C5A059] text-[#0A192F] hover:bg-[#C5A059]/90 px-10 py-7 text-lg font-semibold w-full sm:w-auto" data-testid="final-senior-btn">
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Experienced &middot; 70/30
                </Button>
              </a>
              <a href={MAILTO_JUNIOR}>
                <Button
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 px-10 py-7 text-lg w-full sm:w-auto"
                  data-testid="final-junior-btn"
                >
                  <Users className="w-5 h-5 mr-2" />
                  Ramp Advisor &middot; 50/50
                </Button>
              </a>
            </div>
            <div className="mt-10 space-y-1">
              <a href="mailto:hello@ridgemontfinancial.com" className="block text-white/40 text-sm hover:text-white/60 transition-colors">hello@ridgemontfinancial.com</a>
              <a href="https://substack.com/@retirewithcertainty" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#FF6719] text-sm hover:text-white transition-colors mt-2" data-testid="agents-substack-link">
                Retire With Certainty &middot; Newsletter
              </a>
            </div>
            <p className="text-white/30 text-sm mt-6">Real conversations. No pitch decks.</p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AgentsPage;
